import React from 'react';
import { t } from 'i18next';
import { ArrowLeft } from 'lucide-react';
import { RadixButton, RadixCard } from '@/components/radix';
import { useLoginV3Flow } from './LoginV3FlowProvider';

export default function LoginV3DepartmentCard() {
    const { state, selectDepartment, goNextInFlow, goBackInFlow } = useLoginV3Flow();
    const { locations, selectedIndex } = state.department;

    return (
        <RadixCard className="!p-0 border !border-[#E6E1D9] shadow-xl rounded-md bg-white/95 backdrop-blur-md overflow-hidden flex flex-col">
            <div className="relative bg-white p-5 sm:p-8 pb-4 text-center border-b border-[#F0EDE8] flex-shrink-0">
                <button
                    type="button"
                    onClick={goBackInFlow}
                    aria-label={t('Common.Back')}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-primary-500 transition-colors bg-transparent border-none outline-none cursor-pointer"
                >
                    <ArrowLeft size={20} />
                </button>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
                    {t('Common.LogChooseDepartment')}
                </h1>
                <p className="text-gray-500 text-[11px] sm:text-sm mt-1 font-medium">
                    {t('Common.LoginV3DepartmentSubtitle')}
                </p>
            </div>

            <div className="p-4 sm:p-6 overflow-y-auto custom-scroll max-h-[40vh] sm:max-h-[350px]">
                <div className="space-y-3">
                    {locations.map((location, index) => {
                        const isSelected = selectedIndex === index;
                        const isActive = location.is_active;

                        return (
                            <div
                                key={index}
                                onClick={() => isActive && selectDepartment(index)}
                                className={`
                                            group flex items-center gap-3 px-3 py-2 rounded-xl border-[1px] transition-all duration-200 border-solid border-[#E6E1D9]
                                            ${
                                                !isActive
                                                    ? 'opacity-40 bg-gray-50 border-gray-100 cursor-not-allowed'
                                                    : isSelected
                                                      ? 'border-primary-500 bg-primary-50/30 shadow-md translate-x-1'
                                                      : 'border-[#E6E1D9] hover:border-primary-300 bg-white cursor-pointer'
                                            }
                                        `}
                            >
                                <div
                                    className={`
                                            w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center overflow-hidden border-2
                                            ${isSelected ? 'border-primary-500' : 'border-gray-100'}
                                        `}
                                >
                                    {location.image ? (
                                        <img
                                            src={location.image}
                                            alt={location.name ?? ''}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="bg-gray-100 text-gray-400 font-bold uppercase w-full h-full flex items-center justify-center">
                                            {(location.name ?? '?').charAt(0)}
                                        </div>
                                    )}
                                </div>

                                <div className="flex-1 min-w-0">
                                    <h4
                                        className={`text-sm sm:text-base font-bold truncate ${isSelected ? 'text-primary-600' : 'text-gray-800'}`}
                                    >
                                        {location.name}
                                    </h4>
                                    <p className="text-[10px] sm:text-xs text-gray-500 truncate font-medium">
                                        {location.email ?? ''}
                                    </p>
                                </div>

                                {isActive && (
                                    <div
                                        className={`
                                                w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all
                                                ${isSelected ? 'bg-primary-500 border-primary-500' : 'border-gray-200'}
                                            `}
                                    >
                                        {isSelected && <CheckIcon className="w-3 h-3 text-white" />}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="p-5 sm:p-8 bg-gray-50/50 border-t border-[#F0EDE8] flex-shrink-0">
                <RadixButton
                    variant="primary"
                    className="w-full !h-12 sm:!h-14 !rounded-xl !text-base font-bold shadow-lg shadow-primary-500/10 text-white"
                    onClick={() => void goNextInFlow()}
                    disabled={selectedIndex === null}
                >
                    {t('Common.LogNext')}
                </RadixButton>
            </div>
        </RadixCard>
    );
}

const CheckIcon = ({ className }: { className?: string }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <polyline points="20 6 9 17 4 12" />
    </svg>
);
